// src/components/student/student-fee-pdf/FeeCardDocument.js
import React from 'react';
import './feeCard.css';

/**
 * FeeCardDocument
 * ──────────────
 * A plain-HTML fee card that can be printed / saved-as-PDF via
 * react-to-print — exactly the same pattern as ReportDocument.
 *
 * Props:
 *   feeData  – { student, fee_summary: { total_allocated, total_paid,
 *               total_balance, fee_groups }, term }
 *   school   – { name, logo, motto, address, phone, email }
 */
const FeeCardDocument = React.forwardRef(function FeeCardDocument(
  { feeData, school },
  ref
) {
  if (!feeData) return null;

  const { student, fee_summary, term } = feeData;
  const {
    total_allocated = 0,
    total_paid = 0,
    total_balance = 0,
    fee_groups = [],
  } = fee_summary || {};

  const fmt = (n) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'UGX',
    }).format(parseFloat(n || 0));

  /* ── helpers to pull class/stream name regardless of shape ── */
  const getClassName = () => {
    if (!student) return 'N/A';
    if (student.class_stream_name) return student.class_stream_name;
    if (typeof student.class_stream === 'object' && student.class_stream)
      return (
        student.class_stream.schoolClass?.name ||
        student.class_stream.school_class?.name ||
        'N/A'
      );
    if (typeof student.class_stream === 'string')
      return student.class_stream.split('-')[0] || 'N/A';
    return 'N/A';
  };

  const getStreamName = () => {
    if (!student) return 'N/A';
    if (typeof student.class_stream === 'object' && student.class_stream)
      return student.class_stream.stream?.name || 'N/A';
    if (typeof student.class_stream === 'string')
      return student.class_stream.split('-')[1] || 'N/A';
    return 'N/A';
  };

  const grandAllocated = fee_groups.reduce(
    (s, g) => s + parseFloat(g.allocated || 0),
    0
  );

  return (
    <div className="fc-page" ref={ref}>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="fc-header">
        {/* Logo */}
        <div>
          {school?.logo ? (
            <img src={school.logo} alt="logo" className="fc-logo" />
          ) : (
            <div className="fc-logo-placeholder">LOGO</div>
          )}
        </div>

        {/* Centre info */}
        <div className="fc-header-center">
          <p className="fc-school-name">{school?.name || 'School Name'}</p>
          {school?.motto && (
            <p className="fc-motto">"{school.motto}"</p>
          )}
          {(school?.phone || school?.email) && (
            <p className="fc-contact">
              {[school.phone, school.email].filter(Boolean).join(' | ')}
            </p>
          )}
          {school?.address && (
            <p className="fc-contact">{school.address}</p>
          )}
          <p className="fc-doc-title">STUDENT FEE CARD</p>
        </div>

        {/* Student photo */}
        <div>
          {student?.picture_url ? (
            <img
              src={student.picture_url}
              alt="student"
              className="fc-student-photo"
            />
          ) : (
            <div style={{ width: 60 }} />
          )}
        </div>
      </div>

      {/* ── Student details ─────────────────────────────────── */}
      <div className="fc-student-box">
        <p className="fc-student-name">
          {student?.full_name ||
            `${student?.last_name || ''} ${student?.first_name || ''}`.trim() ||
            'Student'}
        </p>
        <div className="fc-student-grid">
          <div className="fc-field">
            <label>Reg No:</label>
            <span>{student?.registration_no || student?.lin || 'N/A'}</span>
          </div>
          <div className="fc-field">
            <label>Class:</label>
            <span>{getClassName()}</span>
          </div>
          {term && (
            <div className="fc-field">
              <label>Term:</label>
              <span>{term.name} {term.year}</span>
            </div>
          )}
          <div className="fc-field">
            <label>Gender:</label>
            <span>{student?.gender || 'N/A'}</span>
          </div>
          <div className="fc-field">
            <label>Stream:</label>
            <span>{getStreamName()}</span>
          </div>
          <div className="fc-field">
            <label>Contact:</label>
            <span>{student?.guardian_phone || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* ── Summary cards ───────────────────────────────────── */}
      <p className="fc-section-title">Fee Summary</p>
      <div className="fc-summary">
        <div className="fc-summary-card allocated">
          <label>Total Allocated</label>
          <div className="fc-amount">{fmt(total_allocated)}</div>
        </div>
        <div className="fc-summary-card paid">
          <label>Total Paid</label>
          <div className="fc-amount">{fmt(total_paid)}</div>
        </div>
        <div className="fc-summary-card balance">
          <label>Balance Due</label>
          <div className="fc-amount">{fmt(total_balance)}</div>
        </div>
      </div>

      {/* ── Fee groups breakdown ─────────────────────────────── */}
      <p className="fc-section-title">Fee Breakdown</p>
      <table className="fc-table">
        <thead>
          <tr>
            <th>Fee Group</th>
            <th className="num">Allocated</th>
            <th className="num">Paid</th>
            <th className="num">Balance</th>
          </tr>
        </thead>
        <tbody>
          {fee_groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {/* Group row */}
              <tr className="group-row">
                <td>{group.name}</td>
                <td className="num">{fmt(group.allocated)}</td>
                <td className="num">{fmt(group.paid)}</td>
                <td className="num">{fmt(group.balance)}</td>
              </tr>

              {/* Fee-type sub-rows */}
              {group.fee_types?.map((type, ti) => (
                <tr key={ti} className="item-row">
                  <td>• {type.name}</td>
                  <td className="num">{fmt(type.amount)}</td>
                  <td className="num">-</td>
                  <td className="num">-</td>
                </tr>
              ))}
            </React.Fragment>
          ))}

          {/* Grand total */}
          <tr className="total-row">
            <td>TOTAL</td>
            <td className="num">{fmt(grandAllocated)}</td>
            <td className="num">{fmt(total_paid)}</td>
            <td className="num">{fmt(total_balance)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── Footer ──────────────────────────────────────────── */}
      <div className="fc-footer">
        <p>Generated from <span className="fc-footer-brand">School Sync</span></p>
        <p>This is a computer-generated document. No signature is required.</p>
        <p>&copy; {new Date().getFullYear()} School Sync. All rights reserved.</p>
      </div>
    </div>
  );
});

export default FeeCardDocument;